import type { MenuContext, MenuScreen, Opponent } from '../types.js';
import { TABLE_MODES, settingsForTableMode, tableModeFromSettings, type TableMode } from '../types.js';
import type { ActiveSession } from '../../history/types.js';
import { createScreenShell, createMenuHeader, createCrestButton, createCycleBadge, el, withFocusPreserved } from '@bn-games/ui-shell';
import { isDiscordActivity } from '@bn-games/discord-activity';
import {
  modeOpponentLabel,
  type ModeOpponentStrings,
} from '@bn-games/history-core/ui';
import { t } from '../../i18n/I18n.js';

function tableModeLabel(mode: TableMode): string {
  switch (mode) {
    case '2p': return t('playerCount.two');
    case '3p': return t('playerCount.three');
    case '4p': return t('playerCount.four');
    case '4p-2v2': return t('playerCount.fourPartners');
  }
}

function modeOpponentStrings(): ModeOpponentStrings {
  return {
    vsAi: () => t('playMenu.vsAi'),
    local: t('playMenu.localMode'),
    online: (opponent) => t('playMenu.onlineMode', { opponent }),
    opponent: t('common.opponent'),
  };
}

function sessionLabel(session: ActiveSession): string {
  return modeOpponentLabel(
    {
      mode: session.seed.mode,
      opponentName: session.seed.playerName,
    },
    modeOpponentStrings(),
  );
}

type LaunchTarget =
  | { kind: 'local'; opponent: Opponent }
  | { kind: 'online-create' }
  | { kind: 'online-join' };

export function createPlayMenuScreen(): MenuScreen {
  let frame: HTMLElement | null = null;

  function launch(ctx: MenuContext, target: LaunchTarget): void {
    const go = (): void => {
      if (target.kind === 'local') {
        ctx.setOpponent(target.opponent);
        ctx.navigate('game');
      } else if (target.kind === 'online-create') {
        ctx.setOpponent('online');
        ctx.navigate('online-create');
      } else {
        ctx.setOpponent('online');
        ctx.navigate('online-join');
      }
    };
    ctx.discardActiveSession(go);
  }

  function paint(root: HTMLElement, ctx: MenuContext): void {
    while (root.firstChild) root.removeChild(root.firstChild);
    const shell = createScreenShell();
    frame = shell.frame;
    const body = shell.body;
    body.style.justifyContent = 'space-between';

    body.appendChild(createMenuHeader({ title: t('playMenu.title') }));

    const settings = ctx.getSettings();
    const mode = tableModeFromSettings(settings);

    const tableBadge = createCycleBadge<TableMode>({
      label: t('playMenu.playerCountLabel'),
      value: mode,
      options: TABLE_MODES,
      format: tableModeLabel,
      onChange: (next) => {
        ctx.updateSettings(settingsForTableMode(next, settings.variant));
        // Reset pending seat — the prior pick may exceed the new player count.
        ctx.setPendingLocalSeat(0);
        withFocusPreserved(() => paint(root, ctx), root);
      },
    });

    const stack: HTMLElement[] = [];

    // Inside Discord the instance room is the only online room that exists:
    // room codes cannot be exchanged in the iframe, and resuming a room saved
    // by a previous web session would pull the player out of the instance.
    const roomCodeLobby = !isDiscordActivity();

    const session = ctx.getActiveSession();
    const savedOnline = roomCodeLobby ? ctx.getSavedSession() : null;

    if (session) {
      stack.push(
        createCrestButton({
          label: `${t('playMenu.resume')} · ${sessionLabel(session)}`,
          primary: true,
          onClick: () => ctx.resumeActiveGame(),
        }),
      );
    } else if (savedOnline) {
      stack.push(
        createCrestButton({
          label: t('playMenu.resumeOnline', { code: savedOnline.roomCode }),
          primary: true,
          onClick: () => {
            ctx.setOpponent('online');
            ctx.resumeSession();
          },
        }),
      );
    }

    const hasResumeCta = Boolean(session || savedOnline);

    stack.push(
      createCrestButton({
        label: t('playMenu.vsAi'),
        primary: !hasResumeCta,
        onClick: () => launch(ctx, { kind: 'local', opponent: 'ai' }),
      }),
      createCrestButton({
        label: t('playMenu.vsHuman'),
        onClick: () => launch(ctx, { kind: 'local', opponent: 'local' }),
      }),
    );
    if (roomCodeLobby) {
      stack.push(
        createCrestButton({
          label: t('playMenu.createOnline'),
          onClick: () => launch(ctx, { kind: 'online-create' }),
        }),
        createCrestButton({
          label: t('playMenu.joinOnline'),
          onClick: () => launch(ctx, { kind: 'online-join' }),
        }),
      );
    }

    const buttons = el('div', { class: 'button-stack' }, stack);

    const back = el('div', { class: 'button-row back-button-row' }, [
      createCrestButton({ label: t('common.back'), small: true, onClick: () => ctx.navigate('menu') }),
    ]);

    const center = el('div', { class: 'about-center' }, [tableBadge, buttons]);

    body.appendChild(center);
    body.appendChild(back);
    root.appendChild(frame);
  }

  return {
    mount(root, ctx) {
      paint(root, ctx);
    },
    update(ctx) {
      if (frame?.parentElement) paint(frame.parentElement, ctx);
    },
    unmount() {
      frame?.remove();
      frame = null;
    },
  };
}
